import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  useDistrictsQuery,
  useProvincesQuery,
  useToggleDistrictEnabled,
} from "../api/queries";
import { DistrictsTour, useDistrictsTour } from "../tour";
import { DistrictsFilter } from "../ui/DistrictsFilter";
import { DistrictsTable } from "../ui/DistrictsTable";
import { DistrictsToolbar } from "../ui/DistrictsToolbar";

export function DistrictsPage() {
  const nav = useNavigate({ from: "/app/geo/districts" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/geo/districts" });
  const { run, handleJoyrideCallback, startTour } = useDistrictsTour();
  const list = useDistrictsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const provincesQuery = useProvincesQuery({ limit: 500, offset: 0 });
  const provinces = provincesQuery.data?.data ?? [];
  const canManage = useActionPermission(["geo:create"]);
  const toggleEnabled = useToggleDistrictEnabled();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ເມືອງ" },
          ]}
        />
      </Header>
      <Main>
        <DistrictsToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/geo/districts/new" })}
          onStartTour={startTour}
        />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <DistrictsFilter provinces={provinces} />
          <DistrictsTable
            canManage={!!canManage}
            provinces={provinces}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(row) =>
              nav({
                to: "/app/geo/districts/$id/edit",
                params: { id: row.id },
              })
            }
            onToggleEnabled={(id) =>
              toast.promise(toggleEnabled.mutateAsync(id), {
                loading: "ກຳລັງອັບເດດ...",
                success: "ອັບເດດສຳເລັດ",
                error: "ອັບເດດລົ້ມເຫຼວ",
              })
            }
          />
        </div>
        <DistrictsTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
